/**
 * AutomationPage — RC 1.25.B Automation Persistence v1
 *
 * Previously (RC 1.24Y) this page was a client-only surface backed by
 * localStorage. RC 1.25.B flips it to a real persisted backend:
 *
 *   GET   /api/automation/settings   → mode / threshold / paused
 *   PATCH /api/automation/settings   → persist the above
 *   GET   /api/automation/queue      → server-evaluated queue (job_user_state)
 *   GET   /api/automation/events     → append-only activity log
 *   POST  /api/automation/queue/{job_id}/approve  → status=pursuing
 *   POST  /api/automation/queue/{job_id}/dismiss  → status=ignored
 *
 * Honesty rules (RC 1.41 — Mode Definition Refresh):
 *   • Modes keep their canonical labels: Manual Review, Assisted Review,
 *     Automation. Names are not renamed; descriptions clarify cadence
 *     and locus of control.
 *   • NDORFlow never auto-applies — on any plan, in any mode.
 *   • Assisted Review and Automation both run Analyse Fit against the
 *     user's Source CV in the background. Cached fits are free to
 *     reopen; users only pay again if they swap in a different CV.
 *   • Approve / Top-Opportunity click opens the JobApplicationWizard;
 *     pack creation, CV selection and submission stay manual.
 *
 * `includeCover` remains a local UI preference (not in the backend
 * contract for 1.25.B — see candidate_workflow_truth_map.json).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { apiFetch } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import InsufficientUsageModal     from '@/components/InsufficientUsageModal';
import AutoDiscoveryChip from '@/components/AutoDiscoveryChip';
import { toast } from 'sonner';
import {
  Zap, Eye, Shield, Pause, Play, CheckCircle2, XCircle,
  Activity, Sliders, FileText, ArrowRight, Info, Sparkles,
} from 'lucide-react';

// ── Mode mapping ────────────────────────────────────────────────────
// RC 1.29.AB — "Automatic Apply" mode REMOVED entirely to align with
// the product philosophy: NDORFlow reviews and prioritises, the user
// always stays in control. Backend still accepts `automatic` as an
// enum value (we don't touch the API contract) but the UI no longer
// offers it as a selectable option, and any existing user with that
// value stored will be surfaced as Manual Review in the UI.
const UI_TO_API_MODE = {
  manual_review: 'manual',
  assisted_review: 'assisted',
  automatic: 'automatic',
};
const API_TO_UI_MODE = {
  manual: 'manual_review',
  assisted: 'assisted_review',
  automatic: 'automatic',
};

// RC 1.33.D — Plan-gating map. Each UI mode maps to the entitlement
// token surfaced by `/api/account/plan::allowed_account_modes`
// (see core/plans.py). The `automatic` mode card is purely a visual
// placeholder — Automatic Apply was retired in RC 1.29.AB and the
// backend no longer wires a UI click to `automation_mode=automatic`,
// but the card still renders so users can see what their plan
// unlocks. Its click handler is a no-op; clickability is gated here.
const MODE_TO_ENTITLEMENT = {
  manual_review: 'manual',
  assisted_review: 'assist',
  automatic: 'auto',
};

const MODES = [
  {
    key: 'manual_review',
    label: 'Manual Review',
    icon: Eye,
    short: 'You browse, score, and apply yourself.',
    long: 'Browse the jobs catalogue and run Analyse Fit yourself when a role catches your eye. You decide which roles to pursue and create every application pack manually. NDORFlow provides the scoring and tooling; nothing runs in the background.',
  },
  {
    key: 'assisted_review',
    label: 'Assisted Review',
    icon: Shield,
    short: 'NDORFlow analyses fit against your Source CV in batches; you review and apply.',
    long: 'NDORFlow runs Analyse Fit against your Source CV in the background, up to your daily budget, and lists matches that beat your fit threshold in your Review Queue. When you open one, the fit score is already done and free to view; you only pay again if you swap in a different CV before applying. You still pick the CV, generate any cover letter, assemble the pack, and submit each application yourself.',
  },
  {
    key: 'automatic',
    label: 'Automation (Flow)',
    icon: Zap,
    short: 'NDORFlow automatically pre-screens jobs and analyses the strongest matches in the background.',
    long: 'NDORFlow automatically pre-screens jobs and fully analyses the strongest matches in the background. You can pause this at any time. No Credits are used for background analysis.',
  },
];

const THRESHOLD_BAND = (v) => {
  if (v >= 90) return { label: 'Elite only — fewer applications, higher quality.',  tone: 'text-ndor-gold' };
  if (v >= 85) return { label: 'Selective — stronger fit, fewer applications.',     tone: 'text-ndor-info' };
  if (v >= 80) return { label: 'Balanced — wider reach with quality control.',      tone: 'text-ndor-success' };
  return              { label: 'Broad — maximum coverage.',                         tone: 'text-ndor-ink-secondary' };
};

// RC 1.29.AC — `includeCover` was a localStorage-only preference
// (see older candidate workflow truth map). Now persisted on the
// user profile via `/api/profile/preferences` so it follows the
// user across devices. No new endpoint — we reuse the RC 1.27
// preferences surface and rely on the profiles.preferences_json
// jsonb column (migration 023). The frontend still reads/writes a
// single boolean; the storage shape is
//   profiles.preferences_json.include_cover_letter : boolean
// DEFAULT true preserves the pre-slice UI behaviour.

const LEDGER_VISIBLE_CAP = 10;

function formatTs(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch (_) { return iso; }
}

// Convert a backend event row into the same render shape the old
// localStorage ledger used. Backend kinds: mode_changed,
// threshold_changed, paused, resumed, budget_changed, queued,
// approved, dismissed, scan_run.
function renderEvent(e) {
  const modeLabel = (apiMode) =>
    (MODES.find((m) => m.key === API_TO_UI_MODE[apiMode]) || {}).label || apiMode;
  const jobShort = (id) => (id || '').slice(0, 8);
  const p = e.payload_json || {};
  switch (e.kind) {
    case 'mode_changed':
      return <>Switched mode to <span className="text-ndor-gold font-mono-ui">{modeLabel(p.to)}</span>.</>;
    case 'threshold_changed':
      return <>Threshold changed to <span className="font-mono-ui text-ndor-gold">{p.to}</span>.</>;
    case 'budget_changed':
      return <>Daily score budget set to <span className="font-mono-ui text-ndor-gold">{p.to}</span>.</>;
    case 'paused':
      return <><span className="text-ndor-warning">Paused.</span></>;
    case 'resumed':
      return <><span className="text-ndor-success">Resumed.</span></>;
    case 'queued':
      return <>Queued role <span className="font-mono-ui text-ndor-ink-secondary">{jobShort(e.job_id)}</span> at fit <span className="font-mono-ui text-ndor-gold">{p.queue_score != null ? Math.round(Number(p.queue_score) * 100) : '—'}</span>.</>;
    case 'approved':
      return <>Approved role <span className="font-mono-ui text-ndor-ink-secondary">{jobShort(e.job_id)}</span> — pack dialog opened. No application was sent.</>;
    case 'dismissed':
      return <>Dismissed role <span className="font-mono-ui text-ndor-ink-secondary">{jobShort(e.job_id)}</span> from your review queue.</>;
    case 'scan_run':
      return <>Scan: <span className="font-mono-ui text-ndor-ink-secondary">added {p.added || 0}</span>, below-threshold {p.skipped_below_threshold || 0}, terminal {p.skipped_terminal || 0}, budget-capped {p.skipped_budget || 0}.</>;
    default:
      return e.kind;
  }
}

// ── Page ───────────────────────────────────────────────────────────
export default function AutomationPage() {
  const { user } = useAuth();
  // RC 1.41 Phase E — approve / Top-Opportunity 'Generate Application
  // Pack' flows now navigate the user to /jobs/:id so the canonical
  // 4-step Job Application Wizard owns pack creation (RC 1.41 §2:
  // Jobs page owns application creation).
  const navigate = useNavigate();

  // RC 1.33.D — plan gating. Single source of truth is
  // `/api/account/plan::allowed_account_modes`; the page defaults to
  // the most restrictive state (base) until the call resolves.
  const [planCode, setPlanCode]         = useState('base');
  const [allowedModes, setAllowedModes] = useState(['manual']);

  // Settings (server-backed).
  const [mode, setMode]           = useState('manual_review');
  const [threshold, setThreshold] = useState(85);
  const [thresholdDraft, setThresholdDraft] = useState(85); // while dragging
  const [paused, setPaused]       = useState(false);
  // RC 1.25.H.1 — daily auto-score budget, fed into the
  // AutoDiscoveryChip so the denominator reflects the user's
  // actual setting (not a hard-coded 10).
  const [dailyBudget, setDailyBudget] = useState(10);
  const [unmigrated, setUnmigrated] = useState(false);

  // RC 1.29.AC — profile-backed. Loaded from
  // `/api/profile/preferences` on mount, written back on toggle.
  // Default `true` matches the pre-slice localStorage fallback so
  // no user sees a behaviour change on first load.
  const [includeCover, setIncludeCover] = useState(true);

  // Server-backed queue + ledger.
  const [queue, setQueue]   = useState([]);
  const [ledger, setLedger] = useState([]);

  // RC 1.35 — Flow-only Top Opportunities feed. Backed by the new
  // `automation_queue` table (migration 033) populated by the
  // admin-triggered automation loop. Read-only on this page; the user
  // clicks through to /jobs/{id} or starts a pack manually. The
  // page never auto-applies, never auto-generates documents.
  const [topOpps, setTopOpps]               = useState([]);
  const [topOppsEnabled, setTopOppsEnabled] = useState(false);
  const [topOppsLoading, setTopOppsLoading] = useState(false);

  const [primaryCv, setPrimaryCv] = useState(null);
  const [loading, setLoading] = useState(true);
  // Subtle "Scanning opportunities…" indicator while the synchronous
  // queue scan runs on the backend (2–4s typical after a mode /
  // threshold / pause change). Not a spinner — just a quiet label.
  const [scanning, setScanning] = useState(false);

  // RC 1.49.x — Flow loop status surface. Backed by
  // `GET /api/automation/flow-status`. Powers:
  //   • the pause/run toggle (`flow_automation_paused`)
  //   • today's counters (jobs_analyzed, above/below threshold)
  //   • the operator-locked daily cap + remaining
  //   • the preferences-missing warning banner.
  const [flowStatus, setFlowStatus] = useState(null);
  const [flowStatusLoading, setFlowStatusLoading] = useState(false);
  const [flowPauseSaving, setFlowPauseSaving] = useState(false);

  // RC 1.41 Phase E — `packDialogOpen / packDialogJob` state retired.
  // Approve / Top-Opportunity flows now navigate to `/jobs/:id` and
  // pack creation is owned by the wizard mounted on JobDetailPage.

  // ── Loaders ────────────────────────────────────────────────────
  const loadSettings = useCallback(async () => {
    const s = await apiFetch('/api/automation/settings');
    const uiMode = API_TO_UI_MODE[s.automation_mode] || 'manual_review';
    setMode(uiMode);
    setThreshold(s.automation_threshold ?? 85);
    setThresholdDraft(s.automation_threshold ?? 85);
    setPaused(!!s.automation_paused);
    setDailyBudget(Number.isFinite(s.automation_daily_score_budget)
      ? s.automation_daily_score_budget
      : 10);
    setUnmigrated(!!s._unmigrated);
  }, []);

  const loadQueue = useCallback(async () => {
    setScanning(true);
    try {
      const r = await apiFetch('/api/automation/queue');
      setQueue(Array.isArray(r?.items) ? r.items : []);
    } finally {
      setScanning(false);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    const r = await apiFetch('/api/automation/events?limit=50');
    setLedger(Array.isArray(r?.items) ? r.items : []);
  }, []);

  const loadPreferences = useCallback(async () => {
    // RC 1.29.AC — read `include_cover_letter` off the user's
    // profile preferences. Silently tolerate the unmigrated case
    // (RC 1.27 migration 023 may not yet be applied in some envs
    // — keep the default `true`).
    try {
      const r = await apiFetch('/api/profile/preferences');
      const v = r?.preferences?.include_cover_letter;
      if (typeof v === 'boolean') setIncludeCover(v);
    } catch (_e) { /* keep default */ }
  }, []);

  // RC 1.49.x — Flow loop status loader. Always safe to call; the
  // backend gates `flow_loop_eligible` server-side, so non-Flow users
  // get a deterministic shape with `flow_loop_eligible:false`.
  const loadFlowStatus = useCallback(async () => {
    setFlowStatusLoading(true);
    try {
      const r = await apiFetch('/api/automation/flow-status');
      setFlowStatus(r || null);
    } catch (_e) {
      setFlowStatus(null);
    } finally {
      setFlowStatusLoading(false);
    }
  }, []);

  // RC 1.49.x — write the pause toggle. Patches
  // `flow_automation_paused` on `/api/automation/settings`.
  const setFlowPaused = useCallback(async (next) => {
    setFlowPauseSaving(true);
    try {
      await apiFetch('/api/automation/settings', {
        method: 'PATCH',
        body: { flow_automation_paused: !!next },
      });
      toast.success(
        next
          ? 'Flow is paused — no background scoring will run.'
          : 'Flow is running — background scoring resumes.',
      );
      await loadFlowStatus();
    } catch (e) {
      toast.error(`Could not update Flow automation: ${e?.message || e}`);
    } finally {
      setFlowPauseSaving(false);
    }
  }, [loadFlowStatus]);

  // RC 1.33.D — plan gating source-of-truth. Silent fallback to
  // base/['manual'] if the endpoint is unreachable, so base users
  // never accidentally see unlocked controls.
  const loadPlan = useCallback(async () => {
    try {
      const p = await apiFetch('/api/account/plan');
      if (p && typeof p === 'object') {
        setPlanCode(p.plan_code || 'base');
        const allowed = Array.isArray(p.allowed_account_modes)
          ? p.allowed_account_modes
          : ['manual'];
        setAllowedModes(allowed);
      }
    } catch (_e) { /* keep defaults */ }
  }, []);

  // RC 1.35 — Top Opportunities feed (Flow-only). Read-only.
  // Server-side gating means non-Flow callers receive
  // {enabled:false, items:[]} so the section stays hidden.
  //
  // RC 1.37 — also captures `usage_costs.application_pack` and
  // `usage_remaining` so the "Open in wizard" CTA can do
  // a synchronous pre-check without an extra round-trip.
  const [topOppsUsageRemaining, setTopOppsUsageRemaining] = useState(null);
  const [packCost,              setPackCost]              = useState(8); // matches USAGE_COSTS SSOT default

  const loadTopOpportunities = useCallback(async () => {
    setTopOppsLoading(true);
    try {
      const r = await apiFetch('/api/automation/top-opportunities?limit=10');
      setTopOppsEnabled(!!r?.enabled);
      setTopOpps(Array.isArray(r?.items) ? r.items : []);
      if (r?.usage_costs?.application_pack != null) {
        setPackCost(Number(r.usage_costs.application_pack));
      }
      if (r?.usage_remaining != null) {
        setTopOppsUsageRemaining(Number(r.usage_remaining));
      }
    } catch (_e) {
      setTopOppsEnabled(false);
      setTopOpps([]);
    } finally {
      setTopOppsLoading(false);
    }
  }, []);

  // RC 1.41 Phase E — `packDialogQueueId` retired alongside the
  // legacy dialog. The "mark queue row reviewed once a pack lands"
  // hop is deferred to a future server-side reconcile slice; with
  // wizard-owned pack creation the client no longer has the natural
  // post-creation callback. The queue still self-reconciles via
  // `loadTopOpportunities` (server filters status='queued').

  // RC 1.38 — replace the RC 1.37 inline-only warning with a real
  // modal. Numbers come from the backend (`packCost`,
  // `topOppsUsageRemaining`); never hard-coded in copy.
  const [insufficientUsageOpen,           setInsufficientUsageOpen]           = useState(false);
  const [insufficientUsageRequired,       setInsufficientUsageRequired]       = useState(null);
  const [insufficientUsageAvailable,      setInsufficientUsageAvailable]      = useState(null);

  // Initial load — settings + queue + events + primary CV label + prefs.
  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadSettings(),
          loadQueue(),
          loadEvents(),
          loadPreferences(),
          loadPlan(),
          loadTopOpportunities(),
          loadFlowStatus(),
          (async () => {
            const { data } = await supabase
              .from('base_cvs')
              .select('id, original_filename, is_primary')
              .eq('user_id', user.id)
              .eq('is_primary', true)
              .maybeSingle();
            if (alive) setPrimaryCv(data || null);
          })(),
        ]);
      } catch (err) {
        if (alive) toast.error(err?.message || 'Failed to load workflow state.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user?.id, loadSettings, loadQueue, loadEvents, loadPreferences, loadPlan, loadTopOpportunities, loadFlowStatus]);

  useEffect(() => {
    // One-shot migration: if the pre-slice localStorage key is
    // still sitting in the browser, clear it so future reads
    // cannot accidentally shadow the profile-backed value.
    try { localStorage.removeItem('ndor.automation.include_cover_letter'); } catch (_e) { /* ignore */ }
  }, []);

  // ── Settings mutations ─────────────────────────────────────────
  const patchSettings = useCallback(async (patch) => {
    try {
      await apiFetch('/api/automation/settings', { method: 'PATCH', body: patch });
      // Refresh settings + queue (queue scan reacts to mode/threshold
      // changes) + events. Keep them sequential-enough for coherence.
      await Promise.all([loadSettings(), loadQueue(), loadEvents()]);
    } catch (err) {
      toast.error(err?.message || 'Failed to update settings.');
    }
  }, [loadSettings, loadQueue, loadEvents]);

  const handleMode = (nextUi) => {
    if (nextUi === mode) return;
    const meta = MODES.find((m) => m.key === nextUi);
    if (!meta) return;
    // RC 1.33.D — gate by plan. Entitlement tokens:
    //   manual → Base, Focus, Flow
    //   assist → Focus, Flow
    //   auto   → Flow only
    const token = MODE_TO_ENTITLEMENT[nextUi];
    if (token && !allowedModes.includes(token)) return;
    const apiMode = UI_TO_API_MODE[nextUi];
    if (!apiMode) return;
    setMode(nextUi); // optimistic
    // ── RC 1.49.x.WF — Workflow mode is the single master switch.
    // Each card writes BOTH `automation_mode` and `flow_automation_paused`
    // atomically so the user can never end up with a Manual card selected
    // while the background Flow loop runs. The mapping is:
    //   Manual Review     → mode='manual'    + flow_automation_paused=TRUE
    //   Assisted Review   → mode='assisted'  + flow_automation_paused=TRUE
    //   Automation (Flow) → mode='automatic' + flow_automation_paused=FALSE
    // The Flow Loop Status pause toggle below the cards remains the
    // override for users who want to pause without dropping to a
    // different mode (e.g. holiday). When that toggle flips back to
    // running, the Automation (Flow) card stays selected.
    const flowPaused = nextUi !== 'automatic';
    patchSettings({
      automation_mode: apiMode,
      flow_automation_paused: flowPaused,
    });
    // Refresh the Flow Status panel so the badge / metrics / pause
    // toggle UI reflect the new paused state immediately.
    loadFlowStatus();
  };

  const handleThreshold = (vals) => {
    const v = Array.isArray(vals) ? vals[0] : vals;
    setThresholdDraft(v);
  };
  const handleThresholdCommit = (vals) => {
    const v = Array.isArray(vals) ? vals[0] : vals;
    if (v === threshold) return;
    setThreshold(v); // optimistic
    patchSettings({ automation_threshold: v });
  };

  // RC 1.49.x — operator-locked 2026-05-07. When the page is
  // entered with `#review-queue` in the URL (e.g. mobile nav from
  // a deep link), scroll to the queue once the rows have loaded.
  // Two-tick delay so the queue card has actually mounted.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#review-queue') return;
    if (loading) return;
    const t = setTimeout(() => {
      const target = document.getElementById('review-queue');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [loading]);

  const handleCover = async (v) => {
    // RC 1.29.AC — profile-backed. Optimistic update + rollback on
    // error so the toggle UI stays responsive even if the network
    // hiccups.
    const prev = includeCover;
    setIncludeCover(v);
    try {
      await apiFetch('/api/profile/preferences', {
        method: 'POST',
        body: { include_cover_letter: v },
      });
    } catch (err) {
      setIncludeCover(prev);
      toast.error(err?.message || 'Failed to save preference.');
    }
  };

  const handlePause = () => {
    const next = !paused;
    setPaused(next); // optimistic
    patchSettings({ automation_paused: next });
  };

  // ── Queue actions ──────────────────────────────────────────────
  const handleApprove = async (item) => {
    try {
      await apiFetch(`/api/automation/queue/${item.job_id}/approve`, { method: 'POST' });
      // RC 1.41 Phase E — pack creation now lives in the
      // JobApplicationWizard at /jobs/:id. Drop the row from the
      // visible queue, log, and route the user to the job detail
      // page where the wizard takes over.
      setQueue((prev) => prev.filter((q) => q.job_id !== item.job_id));
      loadEvents();
      navigate(`/jobs/${item.job_id}`);
    } catch (err) {
      toast.error(err?.message || 'Failed to approve.');
    }
  };

  const handleDismiss = async (item) => {
    const prev = queue;
    setQueue((q) => q.filter((x) => x.job_id !== item.job_id)); // optimistic
    try {
      await apiFetch(`/api/automation/queue/${item.job_id}/dismiss`, { method: 'POST' });
      loadEvents();
    } catch (err) {
      setQueue(prev); // rollback
      toast.error(err?.message || 'Failed to dismiss.');
    }
  };

  // RC 1.41 Phase E — `handlePackCreated` retired alongside the
  // legacy dialog. Pack creation lives in the wizard on JobDetailPage;
  // the queue / events / Top-Opportunities feeds reconcile naturally
  // on the next page-level refresh.

  // ── Derived ────────────────────────────────────────────────────
  const band = THRESHOLD_BAND(thresholdDraft);
  const modeMeta = MODES.find((m) => m.key === mode) || MODES[0];

  // RC 1.33.D — plan-gating derivation. Centralised so the mode
  // grid, threshold, pause toggle, cover-letter switch, and banner
  // all read from the same truth.
  const planGate = useMemo(() => {
    const isBase = planCode === 'base';
    const isFlow = planCode === 'flow';
    // "automatic" card is visual-only (see RC 1.29.AB): it is never
    // clickable, but its locked-state label varies by plan.
    return {
      isBase,
      isFlow,
      // Whether the page's operational controls (threshold slider,
      // pause toggle, include-cover toggle, review queue actions)
      // are usable. Base users see everything greyed out.
      controlsEnabled: !isBase,
      // Per-mode visual lock state.
      modeDisabled: (uiKey) => {
        if (isBase) return true;                         // base locks everything
        const token = MODE_TO_ENTITLEMENT[uiKey];
        return !(token && allowedModes.includes(token));
      },
      // Label shown on a locked card.
      lockLabel: (uiKey) => {
        if (uiKey === 'automatic') {
          // On Flow the card is fully selectable — no label rendered.
          return isFlow ? null : 'Available on Flow';
        }
        if (isBase) return 'Available on Focus';
        return null;
      },
      banner: isBase
        ? {
            title: 'Assisted Review is available on Focus. Automation (Flow) is available on Flow.',
            body:  'Manual Review is enabled. Focus unlocks Assisted Review (NDORFlow analyses fit against your Source CV in the background). Flow unlocks Automation (NDORFlow automatically pre-screens jobs and fully analyses the strongest matches in the background — no Credits used). NDORFlow never auto-applies on any plan.',
            tone:  'base',
          }
        : isFlow
          ? {
              title: 'Automation (Flow) available',
              body:  'Pick Automation (Flow) to run the background loop, Assisted Review for on-demand batches, or Manual Review for full manual control. Each card is the master switch — your selection here decides whether Flow automation runs. NDORFlow never auto-applies.',
              tone:  'flow',
            }
          : {
              title: 'Assisted Review is enabled',
              body:  'NDORFlow runs Analyse Fit against your Source CV in the background, up to your daily budget, and lists matches above your threshold in your Review Queue. You still pick the CV and submit every application yourself. Automation (Flow) — automatic background analysis with no daily cap and no Credits used — is available on Flow.',
              tone:  'focus',
            },
    };
  }, [planCode, allowedModes]);

  // Queue items have queue_score 0-1; convert to 0-100 int for
  // display. Unscored rows (shouldn't happen in practice, but be safe)
  // show '—'.
  const queueRows = useMemo(() => (queue || []).map((r) => ({
    ...r,
    fit: r.queue_score != null ? Math.round(Number(r.queue_score) * 100) : null,
  })), [queue]);

  // ── Render ─────────────────────────────────────────────────────

  // RC 1.49.x — operator-locked 2026-05-07. Workflow has nothing
  // useful to show a Base user: no Assisted background scoring, no
  // Flow loop, no Top Opportunities, no daily auto-batches. Render
  // a clean upgrade-locked card instead of the live controls. The
  // route still resolves so direct links + screenshots keep working.
  if (planCode === 'base') {
    return (
      <div
        data-testid="automation-base-locked-card"
        className="space-y-6 sm:space-y-8"
      >
        <section className="rounded-2xl border border-ndor-edge bg-gradient-to-br from-ndor-surface-alt/40 to-ndor-surface px-6 sm:px-10 py-10 sm:py-14">
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.22em] text-ndor-gold font-mono-ui flex items-center gap-1.5">
              <Zap className="h-3 w-3" />
              Plan · Base
            </p>
            <h1 className="mt-3 text-3xl sm:text-4xl font-serif-display font-semibold tracking-tight text-ndor-ink">
              Workflow is a paid surface.
            </h1>
            <p className="mt-4 text-base text-ndor-ink-secondary leading-relaxed">
              On Base you run Analyse Fit yourself, two free runs per
              day. Workflow surfaces — Assisted Review, the Review Queue
              and Flow's Top Opportunities — only matter once
              NDORFlow is doing background work for you.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-ndor-ink-secondary">
              <li className="flex items-start gap-2.5">
                <Sparkles className="h-4 w-4 text-ndor-info mt-0.5 shrink-0" />
                <span>
                  <strong className="text-ndor-ink">Focus</strong> unlocks{' '}
                  <strong>Assisted Review</strong> — NDORFlow runs Analyse Fit
                  against your Source CV in the background, drops matches
                  above your threshold into the Review Queue, and never
                  charges your Credits for the background pass.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <Zap className="h-4 w-4 text-ndor-gold mt-0.5 shrink-0" />
                <span>
                  <strong className="text-ndor-ink">Flow</strong> adds{' '}
                  <strong>Automation (Flow)</strong> — continuous
                  background scoring while enabled, paused-by-default,
                  surfaces the strongest matches in Top Opportunities.
                  Pre-screens jobs cheaply first; only survivors reach NDOR.
                  No Credits used for the background pass.
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <Shield className="h-4 w-4 text-ndor-ink-muted mt-0.5 shrink-0" />
                <span>
                  NDORFlow <strong>never auto-applies</strong> and{' '}
                  <strong>never auto-tailors</strong> on any plan.
                </span>
              </li>
            </ul>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link to="/account?tab=plan">
                <Button
                  size="sm"
                  className="w-full sm:w-auto bg-ndor-primary hover:bg-ndor-primary-hover text-ndor-primary-foreground"
                  data-testid="automation-base-locked-upgrade"
                >
                  See plan options
                </Button>
              </Link>
              <Link to="/how-it-works">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto border-ndor-edge bg-ndor-surface-alt hover:bg-ndor-surface text-ndor-ink"
                  data-testid="automation-base-locked-howitworks"
                >
                  How it works
                </Button>
              </Link>
              <Link to="/jobs">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full sm:w-auto text-ndor-ink-secondary hover:text-ndor-ink"
                  data-testid="automation-base-locked-browse-jobs"
                >
                  Browse jobs <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="automation-page" className="space-y-6 sm:space-y-8">
      {/* ── RC 1.33.D — Plan-gated banner. Replaces the earlier
          RC 1.31.A subscription upsell. Single banner drives the
          user's read of what's locked / unlocked. ── */}
      <section
        className={`rounded-2xl border px-6 sm:px-8 py-6 sm:py-7 ${
          planGate.banner.tone === 'flow'
            ? 'border-ndor-gold/40 bg-gradient-to-br from-ndor-gold-soft/50 to-ndor-surface-alt/20'
            : planGate.banner.tone === 'focus'
              ? 'border-ndor-info/30 bg-ndor-info-light/40'
              : 'border-ndor-edge bg-ndor-surface-alt/40'
        }`}
        data-testid="automation-plan-banner"
        data-plan-code={planCode}
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
          <div className="max-w-3xl">
            <p
              className="text-[11px] uppercase tracking-[0.22em] text-ndor-gold font-mono-ui flex items-center gap-1.5"
              data-testid="automation-plan-banner-eyebrow"
            >
              <Zap className="h-3 w-3" />
              Plan · {planCode === 'base' ? 'Base' : planCode === 'focus' ? 'Focus' : 'Flow'}
            </p>
            <h2
              className="mt-2 text-2xl sm:text-3xl font-serif-display font-semibold tracking-tight text-ndor-ink"
              data-testid="automation-plan-banner-title"
            >
              {planGate.banner.title}
            </h2>
            <p
              className="mt-2.5 text-sm text-ndor-ink-secondary leading-relaxed"
              data-testid="automation-plan-banner-body"
            >
              {planGate.banner.body}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
            {planGate.isBase && (
              <Link to="/account?tab=plan" className="order-1">
                <Button
                  size="sm"
                  className="w-full sm:w-auto bg-ndor-primary hover:bg-ndor-primary-hover text-ndor-primary-foreground"
                  data-testid="automation-plan-upgrade-cta"
                >
                  See plan options
                </Button>
              </Link>
            )}
            <Link to="/jobs" className="order-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto border-ndor-edge bg-ndor-surface-alt hover:bg-ndor-surface text-ndor-ink"
                data-testid="automation-back-to-jobs"
              >
                Back to jobs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── RC 1.49.x — Flow Loop Status panel ──────────────────
          Backed by GET /api/automation/flow-status. Surfaces:
            • pause / run toggle (`flow_automation_paused`)
            • today's counters (analysed, above/below threshold)
            • operator-locked daily ceiling + remaining
            • preferences-missing warning (NON-blocking)
          Operator-locked: Flow runs server-side only when this
          toggle is OFF. NO user Credits are charged — background
          scoring is operator-absorbed (RC 1.49.x credit safety mandate). */}
      {planGate.isFlow && flowStatus?.flow_loop_eligible && (
        <Card
          className="border-ndor-gold/40 bg-ndor-surface-alt/30"
          data-testid="flow-loop-status-card"
        >
          <CardHeader className="pb-3 border-b border-ndor-gold/20 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-ndor-gold" />
              <CardTitle className="text-base font-serif-display font-semibold text-ndor-ink">
                Automation (Flow)
              </CardTitle>
              <span
                data-testid="flow-loop-state-badge"
                data-state={flowStatus.flow_automation_paused ? 'paused' : 'running'}
                className={`ml-2 inline-flex items-center text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border font-mono-ui ${
                  flowStatus.flow_automation_paused
                    ? 'border-ndor-edge bg-ndor-surface text-ndor-ink-muted'
                    : 'border-ndor-gold/40 bg-ndor-gold-soft text-ndor-gold'
                }`}
              >
                {flowStatus.flow_automation_paused ? 'Paused' : 'Running'}
              </span>
            </div>
            <Button
              size="sm"
              variant={flowStatus.flow_automation_paused ? 'default' : 'outline'}
              disabled={flowPauseSaving}
              onClick={() => setFlowPaused(!flowStatus.flow_automation_paused)}
              data-testid="flow-loop-pause-toggle"
              className={
                flowStatus.flow_automation_paused
                  ? 'bg-ndor-primary hover:bg-ndor-primary-hover text-ndor-primary-foreground'
                  : 'border-ndor-edge'
              }
            >
              {flowPauseSaving
                ? 'Saving…'
                : flowStatus.flow_automation_paused
                  ? (<><Play className="h-3 w-3 mr-1" /> Turn on</>)
                  : (<><Pause className="h-3 w-3 mr-1" /> Pause</>)}
            </Button>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            <p
              className="text-xs text-ndor-ink-secondary leading-relaxed"
              data-testid="flow-loop-credit-safety-copy"
            >
              NDORFlow automatically pre-screens jobs and fully analyses the
              strongest matches in the background. <span className="text-ndor-ink">
              No Credits are used for background analysis</span> — it's covered
              by your Flow subscription. You only spend Credits when you click
              <em> Reanalyse</em> against a different CV before applying.
              NDORFlow never auto-applies. You can pause this at any time.
            </p>

            {flowStatus.preferences_missing_warning && (
              <div
                className="rounded-md border border-amber-300/40 bg-amber-500/10/60 px-3 py-2 text-[12px] text-amber-200 flex items-start gap-2"
                data-testid="flow-loop-preferences-warning"
              >
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  Add your preferred roles, locations, and industries on
                  <Link to="/profile" className="underline mx-1">your profile</Link>
                  to sharpen ranking. Flow still runs against your CV — preferences
                  refine the order, they don't block scoring.
                </span>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="flow-loop-today-metrics">
              <div className="rounded-md border border-ndor-edge bg-ndor-surface px-3 py-2">
                <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">Analysed today</p>
                <p className="mt-1 text-xl font-serif-display text-ndor-ink" data-testid="flow-loop-jobs-analysed">
                  {flowStatus.today?.jobs_analyzed ?? 0}
                </p>
              </div>
              <div className="rounded-md border border-ndor-edge bg-ndor-surface px-3 py-2">
                <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">Above threshold</p>
                <p className="mt-1 text-xl font-serif-display text-emerald-200" data-testid="flow-loop-jobs-above-threshold">
                  {flowStatus.today?.jobs_above_threshold ?? 0}
                </p>
              </div>
              <div className="rounded-md border border-ndor-edge bg-ndor-surface px-3 py-2">
                <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">Below threshold</p>
                <p className="mt-1 text-xl font-serif-display text-ndor-ink-muted" data-testid="flow-loop-jobs-below-threshold">
                  {flowStatus.today?.jobs_below_threshold ?? 0}
                </p>
              </div>
              <div className="rounded-md border border-ndor-edge bg-ndor-surface px-3 py-2">
                <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">Daily cap</p>
                <p className="mt-1 text-xl font-serif-display text-ndor-ink" data-testid="flow-loop-daily-cap">
                  {flowStatus.today?.daily_cap || '—'}
                  {flowStatus.today?.remaining_today != null && (
                    <span className="text-xs text-ndor-ink-muted font-sans ml-1.5">
                      · {flowStatus.today.remaining_today} left
                    </span>
                  )}
                </p>
              </div>
            </div>

            <p className="text-[11px] text-ndor-ink-muted font-mono-ui" data-testid="flow-loop-helper">
              {flowStatus.flow_automation_paused
                ? 'Flow is paused — no background scoring will run until you turn it on.'
                : `Flow is running. The strongest matches surface in Top Opportunities${
                    flowStatusLoading ? ' (refreshing…)' : ''
                  }.`}
            </p>
          </CardContent>
        </Card>
      )}

      {/* ── RC 1.35 — Flow-only Top Opportunities ──────────────
          Read-only feed of jobs the automation loop has surfaced.
          Server enforces Flow gating; we additionally guard on
          `planGate.isFlow` so the section is genuinely hidden for
          Base/Focus and never even renders an empty state.
          The user remains in control: every action is an explicit
          click. Nothing here generates documents or applies on
          the user's behalf. */}
      {planGate.isFlow && topOppsEnabled && (
        <Card
          className="border-ndor-gold/40 bg-gradient-to-br from-ndor-gold-soft/40 to-ndor-surface-alt/20"
          data-testid="automation-top-opportunities"
        >
          <CardHeader className="pb-2 border-b border-ndor-gold/20 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-ndor-gold" />
              <CardTitle className="text-base font-serif-display font-semibold text-ndor-ink">
                Top Opportunities
              </CardTitle>
              <span
                className="ml-2 inline-flex items-center text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border border-ndor-gold/40 bg-ndor-gold-soft text-ndor-gold font-mono-ui"
                data-testid="automation-top-opportunities-flow-badge"
              >
                Flow
              </span>
            </div>
            <span
              className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui"
              data-testid="automation-top-opportunities-count"
            >
              {topOppsLoading
                ? 'Refreshing…'
                : `${topOpps.length} ${topOpps.length === 1 ? 'role' : 'roles'} surfaced`}
            </span>
          </CardHeader>
          <CardContent className="pt-4">
            <p className="text-xs text-ndor-ink-secondary mb-3 leading-relaxed">
              NDORFlow runs Analyse Fit against your <span className="text-ndor-ink">Source CV</span> continuously
              across the jobs pool and surfaces the strongest matches here. Every fit
              listed below is already done and free to view — click any role to open
              the application wizard. You only pay again if you swap in a different CV
              before applying. NDORFlow never auto-applies.
            </p>
            <ul className="mb-4 space-y-1.5 text-[11px] text-ndor-ink-muted leading-relaxed font-mono-ui" data-testid="automation-subnotes">
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1 w-1 flex-shrink-0 rounded-full bg-ndor-gold/60"></span>
                <span>
                  Background Analyse Fits run under your Flow plan and do not
                  charge user Credits. Flow background scoring is operator-absorbed.
                  You only spend Credits when you re-analyse against a different CV
                  before applying.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1 h-1 w-1 flex-shrink-0 rounded-full bg-ndor-gold/60"></span>
                <span>
                  Automation Analyse Fits are stored as ordinary{' '}
                  <code className="text-[10px]">job_scores</code> rows. If you upload a
                  new Source CV later, prior fits remain free to reopen against the
                  older CV; new analysis against the new CV is a fresh paid action.
                </span>
              </li>
            </ul>
            {topOppsLoading && topOpps.length === 0 ? (
              <p className="py-6 text-center text-sm text-ndor-ink-muted">Loading top opportunities…</p>
            ) : topOpps.length === 0 ? (
              <div className="py-6 text-center space-y-1" data-testid="automation-top-opportunities-empty">
                <p className="text-sm text-ndor-ink">
                  No opportunities yet. The automation loop runs in the background — top
                  matches will appear here as new roles are evaluated.
                </p>
                <p className="text-xs text-ndor-ink-secondary">
                  Make sure you have a primary Source CV uploaded so analysis can run.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-ndor-edge/60">
                {topOpps.map((it) => (
                  <li
                    key={it.id || it.job_id}
                    className="py-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-start sm:gap-4"
                    data-testid={`automation-top-opportunity-row-${it.job_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/jobs/${it.job_id}`}
                        className="text-base sm:text-sm font-medium text-ndor-ink hover:underline break-words sm:truncate block leading-snug"
                        data-testid={`automation-top-opportunity-title-${it.job_id}`}
                      >
                        {it.title || 'Untitled role'}
                      </Link>
                      <p className="text-xs text-ndor-ink-muted mt-0.5">
                        <span data-testid={`automation-top-opportunity-company-${it.job_id}`}>
                          {it.company || '—'}
                        </span>
                        {it.location ? <span> · {it.location}</span> : null}
                      </p>
                      {it.summary ? (
                        <p
                          className="text-xs text-ndor-ink-secondary mt-1.5 line-clamp-2"
                          data-testid={`automation-top-opportunity-summary-${it.job_id}`}
                        >
                          {it.summary}
                        </p>
                      ) : null}
                    </div>
                    <span
                      className={`self-start sm:self-auto inline-flex items-baseline gap-1 text-xs font-mono-ui px-2 py-0.5 rounded border shrink-0 ${
                        Number(it.fit_score ?? 0) >= 90
                          ? 'text-ndor-gold bg-ndor-gold-soft/40 border-ndor-gold/30'
                          : 'text-ndor-info bg-ndor-info-light border-ndor-info/20'
                      }`}
                      data-testid={`automation-top-opportunity-fit-${it.job_id}`}
                    >
                      <span className="text-[9px] uppercase tracking-[0.16em] opacity-70">Fit</span>
                      <span className="text-sm font-semibold tabular-nums">
                        {it.fit_score != null ? Math.round(Number(it.fit_score)) : '—'}
                      </span>
                    </span>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <Link
                        to={`/jobs/${it.job_id}`}
                        className="flex-1 sm:flex-none"
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full border-ndor-edge bg-ndor-surface-alt hover:bg-ndor-surface text-ndor-ink"
                          data-testid={`automation-top-opportunity-review-${it.job_id}`}
                        >
                          Review
                        </Button>
                      </Link>
                      <Button
                        size="sm"
                        className="flex-1 sm:flex-none bg-ndor-primary hover:bg-ndor-primary-hover text-ndor-primary-foreground"
                        onClick={() => {
                          // RC 1.41 Phase E — pack creation now lives
                          // in the JobApplicationWizard at /jobs/:id.
                          // We keep the synchronous insufficient-usage
                          // pre-check (RC 1.37 / 1.38 — values come
                          // from the same /top-opportunities response
                          // that returned this row) so users without
                          // enough credits land in the existing
                          // InsufficientUsageModal instead of being
                          // surprised mid-flow inside the wizard.
                          if (
                            topOppsUsageRemaining != null &&
                            topOppsUsageRemaining < packCost
                          ) {
                            setInsufficientUsageRequired(packCost);
                            setInsufficientUsageAvailable(topOppsUsageRemaining);
                            setInsufficientUsageOpen(true);
                            return;
                          }
                          navigate(`/jobs/${it.job_id}`);
                        }}
                        data-testid={`automation-top-opportunity-pack-${it.job_id}`}
                      >
                        Open in wizard <ArrowRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Hero ── */}
      <section
        className="brand-hex-grid rounded-2xl border border-ndor-edge px-6 sm:px-8 py-7 sm:py-8 relative"
        data-testid="automation-hero"
      >
        <div className="relative z-[1] flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-ndor-gold font-mono-ui">Operational</p>
            <h1 className="mt-1 text-3xl sm:text-4xl font-serif-display font-semibold tracking-tight text-ndor-ink" data-testid="automation-operational-title">
              {mode === 'automatic'
                ? 'Automation (Flow)'
                : mode === 'assisted_review'
                  ? 'Assisted Workflow'
                  : 'Manual Workflow'}
            </h1>
            <p className="mt-1.5 text-sm text-ndor-ink-secondary max-w-xl">
              {mode === 'automatic'
                ? 'NDORFlow automatically pre-screens jobs and fully analyses the strongest matches in the background. You can pause this at any time. No Credits are used for background analysis.'
                : mode === 'assisted_review'
                  ? 'Background Analyse Fit against your Source CV, batched to your daily budget. Matches above your threshold appear in your Review Queue. Background Flow automation is OFF in this mode.'
                  : 'You browse, score, and apply yourself. Nothing runs in the background — Flow automation is OFF in this mode.'}
            </p>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] px-2 py-1 rounded-md border border-ndor-gold/40 bg-ndor-gold-soft text-ndor-gold font-mono-ui"
                data-testid="automation-mode-badge"
              >
                <modeMeta.icon className="h-3 w-3" />
                Mode · {modeMeta.label}
              </span>
              <span
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-2 py-1 rounded-md border border-ndor-edge bg-ndor-surface-alt text-ndor-ink-secondary font-mono-ui"
                data-testid="automation-threshold-badge"
              >
                Threshold · {threshold}
              </span>
              {paused && (
                <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-2 py-1 rounded-md border border-ndor-warning/40 bg-ndor-warning-light text-ndor-warning font-mono-ui" data-testid="automation-paused-badge">
                  Paused
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* RC 1.49.x.WF — Pause/Resume only applies to Assisted &
                Automation (Flow). Manual Review IS the "off" state, so
                disable the toggle there to remove the false-affordance
                of clicking it in Manual mode. */}
            {(() => {
              const pauseApplies = mode === 'assisted_review' || mode === 'automatic';
              const pauseDisabled = !planGate.controlsEnabled || !pauseApplies;
              return (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePause}
                  disabled={pauseDisabled}
                  aria-disabled={pauseDisabled}
                  data-testid="automation-pause-toggle"
                  data-pause-applies={pauseApplies ? 'true' : 'false'}
                  title={pauseApplies
                    ? undefined
                    : 'Pause/Resume only applies to Assisted Review and Automation (Flow). Manual Review runs nothing in the background.'}
                  className={`border-ndor-edge ${paused ? 'bg-ndor-surface text-ndor-success' : 'bg-ndor-surface-alt text-ndor-ink'} hover:bg-ndor-surface ${pauseDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {paused ? <><Play className="h-4 w-4 mr-1.5"/> Resume workflow</> : <><Pause className="h-4 w-4 mr-1.5"/> Pause workflow</>}
                </Button>
              );
            })()}
            <Button
              size="sm"
              onClick={() => {
                // RC 1.49.x — operator-locked 2026-05-07. The
                // Review Queue lives on this same page; navigating
                // away to /jobs lost mobile users completely. Scroll
                // to the local section so the user sees actionable
                // queue rows immediately. `scroll-mt-24` on the
                // anchor keeps the heading clear of the sticky nav.
                const target = document.getElementById('review-queue');
                if (target) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="bg-ndor-primary hover:bg-ndor-primary-hover text-ndor-primary-foreground"
              data-testid="automation-jump-to-queue"
            >
              Jump to Review Queue
            </Button>
          </div>
        </div>
      </section>

      {/* ── Status banner ── */}
      <div
        className="rounded-xl border border-ndor-warning/30 bg-ndor-warning-light/50 px-4 py-3 flex items-start gap-3"
        data-testid="automation-status-banner"
      >
        <Info className="h-4 w-4 text-ndor-warning mt-0.5 shrink-0" />
        <div className="text-sm">
          <p className="text-ndor-ink font-medium">
            This system helps you review and act — you remain in control.
          </p>
          <p className="text-ndor-ink-secondary text-xs mt-0.5">
            NDOR organises and prioritises opportunities; every decision and every application
            stays with you. Nothing is sent on your behalf.
          </p>
        </div>
      </div>

      {unmigrated && (
        <div
          className="rounded-xl border border-ndor-error/30 bg-ndor-error-light/40 px-4 py-3 text-sm text-ndor-ink"
          data-testid="automation-unmigrated-banner"
        >
          Workflow backend migrations are not yet applied on this environment.
          Settings and queue will return defaults until an admin applies migrations 018/019/020.
        </div>
      )}

      {/* RC 1.25.H.1 — auto-discovery activity chip. Only renders in
          Assisted Review; pure derivation from the events feed. */}
      <AutoDiscoveryChip
        mode={mode}
        events={ledger}
        dailyBudget={dailyBudget}
      />

      {/* ── Mode selector + Rules ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
        <Card className="border-ndor-edge bg-ndor-surface" data-testid="automation-mode-card">
          <CardHeader className="pb-2 border-b border-ndor-edge">
            <CardTitle className="text-base font-serif-display font-semibold text-ndor-ink flex items-center gap-2">
              <Zap className="h-4 w-4 text-ndor-gold" /> Workflow Mode
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            {MODES.map((m) => {
              const active = m.key === mode;
              const disabled = planGate.modeDisabled(m.key);
              const lockLabel = planGate.lockLabel(m.key);
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => !disabled && handleMode(m.key)}
                  disabled={disabled}
                  data-testid={`automation-mode-${m.key}`}
                  data-mode-locked={disabled ? 'true' : 'false'}
                  aria-pressed={active}
                  aria-disabled={disabled}
                  className={`relative text-left rounded-xl border p-4 transition-all brand-card-hover
                    ${disabled
                      ? 'border-ndor-edge bg-ndor-surface-alt/30 opacity-60 cursor-not-allowed grayscale'
                      : active
                        ? 'border-ndor-gold/60 bg-ndor-gold-soft/30'
                        : 'border-ndor-edge bg-ndor-surface-alt/50 hover:border-ndor-gold/30'}`}
                >
                  {active && !disabled && (
                    <span aria-hidden className="absolute top-3 right-3 h-2 w-2 rounded-full bg-ndor-gold" />
                  )}
                  {disabled && lockLabel && (
                    <span
                      className="absolute top-3 right-3 text-[9px] font-mono-ui uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border border-ndor-edge bg-ndor-surface text-ndor-ink-muted"
                      data-testid={`automation-mode-${m.key}-lock-label`}
                    >
                      {lockLabel}
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <m.icon className={`h-4 w-4 ${active ? 'text-ndor-gold' : 'text-ndor-ink-muted'}`} />
                    <p className="text-sm font-serif-display font-semibold text-ndor-ink">{m.label}</p>
                  </div>
                  <p className="mt-2 text-xs text-ndor-ink-secondary leading-relaxed">{m.long}</p>
                </button>
              );
            })}
          </CardContent>
        </Card>

        <Card className="border-ndor-edge bg-ndor-surface" data-testid="automation-rules-card">
          <CardHeader className="pb-2 border-b border-ndor-edge">
            <CardTitle className="text-base font-serif-display font-semibold text-ndor-ink flex items-center gap-2">
              <Sliders className="h-4 w-4 text-ndor-gold" /> Rules & Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5 space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">Source CV</p>
              <p className="text-sm text-ndor-ink mt-1 truncate" data-testid="automation-rule-source-cv">
                {primaryCv?.original_filename || <span className="text-ndor-ink-muted">No primary Source CV set yet.</span>}
              </p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">Include Cover Letter</p>
                <p className="text-xs text-ndor-ink-secondary mt-0.5">Saved to your profile — used when you generate a pack.</p>
              </div>
              <Switch
                checked={includeCover}
                onCheckedChange={handleCover}
                disabled={!planGate.controlsEnabled}
                aria-disabled={!planGate.controlsEnabled}
                data-testid="automation-cover-toggle"
              />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">Target locations / role preferences</p>
              <p className="text-xs text-ndor-ink-muted mt-0.5">Coming next — will use your profile preferences.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Threshold ── */}
      <Card className="border-ndor-edge bg-ndor-surface" data-testid="automation-threshold-card">
        <CardHeader className="pb-2 border-b border-ndor-edge">
          <CardTitle className="text-base font-serif-display font-semibold text-ndor-ink flex items-center gap-2">
            <Activity className="h-4 w-4 text-ndor-gold" /> Fit Threshold
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-5">
          <div className="flex items-end justify-between gap-4 mb-3">
            <p className="text-5xl font-serif-display font-semibold text-ndor-ink leading-none" data-testid="automation-threshold-value">
              {thresholdDraft}
            </p>
            <p className={`text-sm ${band.tone}`} data-testid="automation-threshold-helper">
              {band.label}
            </p>
          </div>
          <Slider
            min={70} max={95} step={1}
            value={[thresholdDraft]}
            onValueChange={handleThreshold}
            onValueCommit={handleThresholdCommit}
            disabled={!planGate.controlsEnabled}
            aria-disabled={!planGate.controlsEnabled}
            data-testid="automation-threshold-slider"
            className={`my-2 ${!planGate.controlsEnabled ? 'opacity-50 pointer-events-none' : ''}`}
          />
          <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui mt-1">
            <span>Broad · 70</span>
            <span>Balanced · 80</span>
            <span>Selective · 85</span>
            <span>Elite · 90+</span>
          </div>
          <p className="text-xs text-ndor-ink-secondary mt-3">
            Lower threshold = broader coverage. Higher threshold = narrower, higher-quality targeting.
            Only jobs with a fit score at or above the threshold reach the approval queue.
          </p>
        </CardContent>
      </Card>

      {/* ── Review Queue + Activity Log ── */}
      <div
        id="review-queue"
        className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch scroll-mt-24"
      >

      {/* ── Review queue ── */}
      <Card
        className="border-ndor-edge bg-ndor-surface h-full flex flex-col min-h-[22rem]"
        data-testid="automation-queue-card"
      >
        <CardHeader className="pb-2 border-b border-ndor-edge flex flex-row items-center justify-between">
          <CardTitle className="text-base font-serif-display font-semibold text-ndor-ink flex items-center gap-2">
            <FileText className="h-4 w-4 text-ndor-gold" />
            {mode === 'assisted_review' ? 'Review Queue' : 'Prioritised Opportunities'}
          </CardTitle>
          <span className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui" data-testid="automation-queue-status">
            {scanning
              ? 'Scanning opportunities…'
              : `${queueRows.length} ${queueRows.length === 1 ? 'role' : 'roles'} ≥ ${threshold}`}
          </span>
        </CardHeader>
        <CardContent className="pt-2 flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <p className="py-8 text-center text-sm text-ndor-ink-muted">Loading opportunities…</p>
          ) : queueRows.length === 0 ? (
            <div className="py-8 text-center space-y-1" data-testid="automation-queue-empty">
              <p className="text-sm text-ndor-ink">
                {mode === 'manual_review'
                  ? 'Manual Review does not surface a review queue. Switch to Assisted Review to let NDORFlow run Analyse Fit against your Source CV in the background.'
                  : paused
                    ? 'Workflow is paused — no new items are being queued.'
                    : 'No scored jobs currently meet your selected threshold.'}
              </p>
              <p className="text-xs text-ndor-ink-secondary">
                Lower the threshold, or run Analyse Fit on more jobs to see them here.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-ndor-edge">
              {queueRows.map((item) => (
                <li
                  key={item.job_id}
                  className="py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3"
                  data-testid={`automation-queue-row-${item.job_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/jobs/${item.job_id}`}
                      className="text-base sm:text-sm font-medium text-ndor-ink hover:underline break-words sm:truncate block leading-snug"
                      data-testid={`automation-queue-title-${item.job_id}`}
                    >
                      {item.title || 'Untitled role'}
                    </Link>
                    <p className="text-xs text-ndor-ink-muted sm:truncate mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span>{item.company || '—'}</span>
                      {item.source_kind === 'platform' && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded border border-ndor-info/30 bg-ndor-info-light text-[9px] font-mono-ui uppercase tracking-[0.16em] text-ndor-info"
                          data-testid={`automation-queue-source-platform-${item.job_id}`}
                          title="Public role from a shared platform source"
                        >
                          Public
                        </span>
                      )}
                    </p>
                  </div>

                  <span
                    className={`self-start sm:self-auto inline-flex items-baseline gap-1 text-xs font-mono-ui px-2 py-0.5 rounded border shrink-0 ${
                      (item.fit ?? 0) >= 90
                        ? 'text-ndor-gold bg-ndor-gold-soft/40 border-ndor-gold/30'
                        : 'text-ndor-info bg-ndor-info-light border-ndor-info/20'
                    }`}
                    data-testid={`automation-queue-fit-${item.job_id}`}
                  >
                    <span className="text-[9px] uppercase tracking-[0.16em] opacity-70">Fit</span>
                    <span className="text-sm font-semibold tabular-nums">{item.fit ?? '—'}</span>
                  </span>

                  {mode === 'assisted_review' ? (
                    <div className="flex gap-2 w-full sm:w-auto">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 sm:flex-none border-ndor-success/40 text-ndor-success bg-ndor-success-light/40 hover:bg-ndor-success-light hover:border-ndor-success"
                        onClick={() => handleApprove(item)}
                        data-testid={`automation-queue-approve-${item.job_id}`}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 sm:flex-none border-ndor-error/40 text-ndor-error bg-ndor-error-light/40 hover:bg-ndor-error-light hover:border-ndor-error"
                        onClick={() => handleDismiss(item)}
                        data-testid={`automation-queue-decline-${item.job_id}`}
                      >
                        <XCircle className="h-4 w-4 mr-1.5" /> Dismiss
                      </Button>
                    </div>
                  ) : (
                    <Link to={`/jobs/${item.job_id}`} className="w-full sm:w-auto">
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full sm:w-auto border-ndor-edge bg-ndor-surface-alt hover:bg-ndor-surface text-ndor-ink"
                      >
                        Open <ArrowRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* ── Activity Log ── */}
      <Card
        className="border-ndor-edge bg-ndor-surface h-full flex flex-col min-h-[22rem]"
        data-testid="automation-ledger-card"
      >
        <CardHeader className="pb-2 border-b border-ndor-edge flex flex-row items-center justify-between">
          <CardTitle className="text-base font-serif-display font-semibold text-ndor-ink flex items-center gap-2">
            <Activity className="h-4 w-4 text-ndor-gold" /> Activity Log
          </CardTitle>
          <span
            className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui"
            data-testid="automation-ledger-count"
          >
            {ledger.length === 0
              ? 'No events yet'
              : `${ledger.length} ${ledger.length === 1 ? 'event' : 'events'}${ledger.length > LEDGER_VISIBLE_CAP ? ' · scroll for more' : ''}`}
          </span>
        </CardHeader>
        <CardContent className="pt-2 flex-1 min-h-0 flex flex-col">
          {ledger.length === 0 ? (
            <p className="py-8 text-center text-sm text-ndor-ink-secondary" data-testid="automation-ledger-empty">
              No activity yet. Your settings changes and queue actions will be logged here.
              Nothing in this log represents a submitted application.
            </p>
          ) : (
            <div
              className="flex-1 min-h-0 overflow-y-auto pr-1"
              data-testid="automation-ledger-scroll"
            >
              <ul className="divide-y divide-ndor-edge">
                {ledger.map((e, i) => (
                  <li
                    key={e.id || i}
                    className="py-2.5 flex items-start gap-3"
                    data-testid={`automation-ledger-row-${e.kind}-${i}`}
                  >
                    <span className="text-[11px] font-mono-ui text-ndor-ink-muted tracking-wide whitespace-nowrap pt-0.5">
                      {formatTs(e.created_at)}
                    </span>
                    <span className="text-sm text-ndor-ink flex-1">
                      {renderEvent(e)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      </div>

      {/* ── Safety / control messaging ── */}
      <div className="rounded-xl border border-ndor-edge bg-ndor-surface-alt/50 px-5 py-4 flex items-start gap-3" data-testid="automation-safety-note">
        <Shield className="h-4 w-4 text-ndor-gold mt-0.5 shrink-0" />
        <div className="text-sm">
          <p className="text-ndor-ink font-medium">You remain in control.</p>
          <p className="text-ndor-ink-secondary text-xs mt-0.5 leading-relaxed">
            NDORFlow never submits an application on your behalf — on any plan, in any mode.
            Approving a role in Assisted Review or clicking a row in Top Opportunities opens
            that job's detail page; you pick the CV, run any extra Analyse Fit, generate the
            cover letter, assemble the pack, and submit each application yourself. Pause at
            any time to stop new items being scored.
          </p>
        </div>
      </div>

      {/* RC 1.41 Phase E — `StartApplicationPackDialog` mount retired.
          Approve / Top-Opportunity flows navigate to /jobs/:id and the
          JobApplicationWizard takes over pack creation. */}

      {/* ── RC 1.38 — Insufficient-usage modal for Top Opportunities.
            Replaces the RC 1.37 inline-only warning. Numeric values
            are sourced from /api/automation/top-opportunities (server
            SSOT — packCost, topOppsUsageRemaining); never hard-coded
            in copy. Modal CTA routes to /account?tab=plan&addUsage=1
            so the package selector pre-opens. ── */}
      <InsufficientUsageModal
        open={insufficientUsageOpen}
        onOpenChange={setInsufficientUsageOpen}
        requiredUsage={insufficientUsageRequired}
        availableUsage={insufficientUsageAvailable}
        actionLabel="this application pack"
      />
    </div>
  );
}
